<script setup lang="ts">
import { defineProps, onMounted, ref, watch, nextTick } from 'vue'
import { useAccountStore } from '@caks/shared/store/store.account'
import { IconCart, IconCross } from '@caks/uikit/icon/'

const store = useAccountStore()

interface Props {
  desktop?: boolean
}

const props = defineProps<Props>()

const toasts = ref<any[]>([])
const nextToastId = ref<number>(0)
const animationDuration = ref<number>(6000)
const swipeThreshold = ref<number>(50)
const touchStartX = ref<number | null>(null)
const swipeDirection = ref<'left' | 'right' | null>(null)

const removeToast = (toastId: number, direction: 'left' | 'right' | null = null) => {
  const toastIndex = toasts.value.findIndex((toast) => toast.id === toastId)
  if (toastIndex === -1) return
  const toast = toasts.value[toastIndex]
  if (direction) {
    toast.isSwiping = true
    toast.swipeDirection = direction
    setTimeout(() => {
      toasts.value = toasts.value.filter((toast) => toast.id !== toastId)
    }, 200)
  } else {
    toasts.value = toasts.value.filter((toast) => toast.id !== toastId)
  }
}

const handleTouchStart = (event: TouchEvent, toast: any) => {
  if (event.touches.length === 1) {
    touchStartX.value = event.touches[0].clientX
    swipeDirection.value = null
  }
}

const handleTouchMove = (event: TouchEvent, toast: any) => {
  if (touchStartX.value === null) return
  const touchX = event.touches[0].clientX
  const deltaX = touchX - touchStartX.value
  if (Math.abs(deltaX) > swipeThreshold.value && !swipeDirection.value) {
    if (deltaX > 0) {
      swipeDirection.value = 'right'
    } else {
      swipeDirection.value = 'left'
    }
    removeToast(toast.id, swipeDirection.value)
    touchStartX.value = null
  }
}

const handleTouchEnd = () => {
  touchStartX.value = null
  swipeDirection.value = null
}

const addToast = (name: string, description: string, photo: string) => {
  const toastId = (nextToastId.value += nextToastId.value + 1)
  const newToast = {
    id: toastId,
    name,
    description,
    photo,
    isVisible: true,
    isSwiping: false,
    swipeDirection: null,
    timeoutId: null,
  }
  toasts.value.push(newToast)

  nextTick(() => {
    newToast.timeoutId = setTimeout(() => {
      removeToast(toastId)
    }, animationDuration.value)
  })
}

watch(
  () => store.toastList,
  (newCart, oldCart) => {
    const item = store.toastList[store.toastList.length - 1]
    if (item) {
      addToast(item.item.name, item.item.description, item.item.main_media)
    }
  },
  { deep: true }
)
</script>

<template>
  <div class="custom-toast-wrapper" :class="{ 'toast-desktop': desktop }">
    <div
      v-for="toast in toasts"
      :key="toast.id"
      class="custom-toast"
      :class="{
        'custom-toast--visible': toast.isVisible,
        'custom-toast--swiping': toast.isSwiping,
        'custom-toast--swipe-left': toast.swipeDirection === 'left',
        'custom-toast--swipe-right': toast.swipeDirection === 'right',
      }"
      @touchstart="handleTouchStart($event, toast)"
      @touchmove="handleTouchMove($event, toast)"
      @touchend="handleTouchEnd"
    >
      <div class="custom-toast__photo">
        <img :src="toast.photo" alt="photo" />
      </div>
      <div class="custom-toast__info">
        <h2>{{ toast.name }}</h2>
        <p>{{ toast.description }}</p>
        <span> <icon-cart /> Товар добавлен в корзину </span>
      </div>
      <div v-if="desktop" @click="removeToast(toast.id)" class="custom-toast__close">
        <icon-cross />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@keyframes fadeOut {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.custom-toast-wrapper {
  width: calc(100% - 32px);
  position: fixed;
  z-index: 17;
  top: 56px;
  left: 50%;
  transform: translateX(-50%);

  &.toast-desktop {
    left: auto;
    right: 30px;
    top: 137px;
    width: 461px;
    transform: none;
  }
}

.custom-toast {
  background: #fafafa;
  box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.11);
  border-radius: 12px;
  width: 100%;
  display: flex;
  gap: 34px;
  padding: 13px 20px 16px 20px;
  margin-bottom: 20px;
  animation: fadeOut 6s ease-in-out forwards;

  .toast-desktop & {
    padding: 20px;
    align-items: center;
    gap: 23px;
  }

  &--swiping {
    transition:
      transform 0.3s ease-in-out,
      opacity 0.3s ease-in-out;
    pointer-events: none;
    opacity: 0;
  }

  &--swipe-left {
    transform: translateX(-150px);
    transition: 0.3s;
    opacity: 0;
  }

  &--swipe-right {
    transform: translateX(150px);
    transition: 0.3s;
    opacity: 0;
  }

  &__info {
    max-width: calc(100% - 90px);

    h2 {
      color: #0d0802;
      @include h5-6;
      line-height: 18px;
    }

    p {
      @include h7-1;
      line-height: 16px;
      color: #767676;
      margin-bottom: 6px;
      white-space: nowrap;
      overflow: hidden;
      line-height: 16px;
      text-overflow: ellipsis;
    }

    span {
      @include h6-3;
      font-weight: 500;
      color: #009e50;
      display: flex;
      gap: 12px;
      align-items: center;

      svg {
        width: 16px;
        height: 16px;
        --svg-col: #009e50;
      }
    }

    .toast-desktop & {
      max-width: calc(100% - 150px);

      h2 {
        color: #0d0802;
        @include h3-5;
      }

      p {
        margin: 5px 0 16px 0;
        color: #767676;
        @include h5-2;
      }

      span {
        @include h5-6;
      }
    }
  }

  &__photo {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    .toast-desktop & {
      width: 116px;
      height: 116px;
    }
  }

  &__close {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 20px;
    height: 20px;
    cursor: pointer;
  }
}
</style>
